import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { TextField, ToggleSwitch } from "@/components/base/FormControls";
import type { Category } from "@/types";

interface CatalogForm {
  show_prices: boolean;
  show_stock: boolean;
  show_whatsapp: boolean;
  whatsapp_number: string;
  visible_categories: string[];
}

const EMPTY: CatalogForm = {
  show_prices: true,
  show_stock: false,
  show_whatsapp: true,
  whatsapp_number: "",
  visible_categories: [],
};

export default function CatalogTab() {
  const toast = useToast();
  const { profile } = useAuth();
  const [form, setForm] = useState<CatalogForm>(EMPTY);
  const [categories, setCategories] = useState<Category[]>([]);
  const [rowId, setRowId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, categoriesRes] = await Promise.all([
        supabase
          .from("catalog_settings")
          .select("id, show_prices, show_stock, show_whatsapp, whatsapp_number, visible_categories")
          .limit(1)
          .maybeSingle(),
        supabase.from("product_categories").select("id, name, description, active").order("name"),
      ]);

      if (settingsRes.error) {
        toast.error("No se pudo cargar la configuración del catálogo.");
        return;
      }
      if (settingsRes.data) {
        const data = settingsRes.data;
        setRowId(data.id);
        setForm({
          show_prices: data.show_prices ?? true,
          show_stock: data.show_stock ?? false,
          show_whatsapp: data.show_whatsapp ?? true,
          whatsapp_number: data.whatsapp_number ?? "",
          visible_categories: Array.isArray(data.visible_categories)
            ? (data.visible_categories as string[])
            : [],
        });
      }
      setCategories((categoriesRes.data ?? []) as Category[]);
    } catch {
      toast.error("No se pudo cargar la configuración del catálogo.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCategory = (id: string) => {
    setForm((prev) => ({
      ...prev,
      visible_categories: prev.visible_categories.includes(id)
        ? prev.visible_categories.filter((item) => item !== id)
        : [...prev.visible_categories, id],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        show_prices: form.show_prices,
        show_stock: form.show_stock,
        show_whatsapp: form.show_whatsapp,
        whatsapp_number: form.whatsapp_number.trim() || null,
        visible_categories: form.visible_categories,
        updated_at: new Date().toISOString(),
      };
      const query = rowId
        ? supabase.from("catalog_settings").update(payload).eq("id", rowId)
        : supabase.from("catalog_settings").insert(payload);
      const { error } = await query;
      if (error) {
        toast.error("No se pudo guardar la configuración del catálogo.");
        return;
      }
      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: "configurar",
        entity: "catalog_settings",
        entityId: rowId ?? "catalog_settings",
        newValue: { show_prices: payload.show_prices, show_whatsapp: payload.show_whatsapp },
      });
      if (!rowId) await load();
      toast.success("Configuración del catálogo guardada.");
    } catch {
      toast.error("Ocurrió un error inesperado.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-lg"></i>
        Cargando configuración del catálogo...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-store-3-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Catálogo público</h3>
        </div>
        <ToggleSwitch
          label="Mostrar precios"
          description="Muestra el precio de venta de los productos en el catálogo."
          checked={form.show_prices}
          onChange={(checked) => setForm((prev) => ({ ...prev, show_prices: checked }))}
        />
        <ToggleSwitch
          label="Mostrar disponibilidad"
          description="Muestra si el producto está disponible sin revelar el stock interno."
          checked={form.show_stock}
          onChange={(checked) => setForm((prev) => ({ ...prev, show_stock: checked }))}
        />
        <ToggleSwitch
          label="Botón de WhatsApp"
          description="Muestra un botón de contacto por WhatsApp en cada producto."
          checked={form.show_whatsapp}
          onChange={(checked) => setForm((prev) => ({ ...prev, show_whatsapp: checked }))}
        />
        <div className="sm:max-w-xs">
          <TextField
            label="Número de WhatsApp"
            name="whatsapp_number"
            value={form.whatsapp_number}
            onChange={(value) => setForm((prev) => ({ ...prev, whatsapp_number: value }))}
            inputMode="tel"
            placeholder="18090000000"
            hint="Incluye el código de país, sin espacios."
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">
            Categorías visibles
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            Si no seleccionas ninguna, se mostrarán todas las categorías activas.
          </p>
        </div>
        {categories.length === 0 ? (
          <p className="rounded-md border border-dashed border-background-300 px-4 py-6 text-center text-sm text-foreground-500">
            Aún no hay categorías creadas. Créalas en el módulo de Productos.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const selected = form.visible_categories.includes(category.id);
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    selected
                      ? "border-primary-300 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className={`${selected ? "ri-check-line" : "ri-add-line"} text-sm`}></i>
                  {category.name}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
        >
          {saving ? (
            <i className="ri-loader-4-line animate-spin text-base"></i>
          ) : (
            <i className="ri-save-3-line text-base"></i>
          )}
          Guardar cambios
        </button>
      </div>
    </div>
  );
}